import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';

import {
  CardWrapper,
  IconCircle,
  ContentBlock,
  TitleText,
  ValueText,
  UnitText,
  TrendBadge,
  SparklineContainer,
  LimitsRow,
  LimitLabel,
} from './KpiCardStyle';

// Tree-shake: only line + grid + canvas
echarts.use([LineChart, GridComponent, CanvasRenderer]);

const formatDisplay = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'number') return value.toLocaleString('en-US');
  return String(value);
};

const TrendIcon = ({ trend }) => {
  if (trend > 0) return <ArrowUpwardIcon sx={{ fontSize: 14 }} />;
  if (trend < 0) return <ArrowDownwardIcon sx={{ fontSize: 14 }} />;
  return <RemoveIcon sx={{ fontSize: 14 }} />;
};

const KpiCard = ({
  title,
  value,
  unit,
  icon,
  accentColor,
  trend,
  trendLabel,
  sparklineData,
  min,
  max,
  onClick,
}) => {
  const sparkOption = useMemo(() => {
    if (!sparklineData || sparklineData.length < 2) return null;
    const color = accentColor || '#ff9900';
    return {
      grid: { top: 0, right: 0, bottom: 0, left: 0 },
      xAxis: { show: false, type: 'category' },
      yAxis: { show: false, type: 'value' },
      series: [
        {
          type: 'line',
          data: sparklineData,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2, color },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: color + '26' },
                { offset: 1, color: 'transparent' },
              ],
            },
          },
        },
      ],
    };
  }, [sparklineData, accentColor]);

  const hasLimits = min !== undefined && max !== undefined && min !== 'N/A' && max !== 'N/A';

  return (
    <CardWrapper accentColor={accentColor} onClick={onClick} role={onClick ? 'button' : undefined}
      style={hasLimits ? { paddingBottom: 30 } : undefined}>
      {icon && <IconCircle accentColor={accentColor}>{icon}</IconCircle>}

      <ContentBlock>
        <TitleText>{title}</TitleText>
        <div>
          <ValueText>{formatDisplay(value)}</ValueText>
          {unit && <UnitText>{unit}</UnitText>}
        </div>
        {trend != null && (
          <TrendBadge trend={trend}>
            <TrendIcon trend={trend} />
            {trendLabel || `${trend > 0 ? '+' : ''}${trend}%`}
          </TrendBadge>
        )}
      </ContentBlock>

      {sparkOption && (
        <SparklineContainer>
          <ReactEChartsCore
            echarts={echarts}
            option={sparkOption}
            style={{ height: 48, width: 80 }}
            opts={{ renderer: 'canvas' }}
            notMerge
            lazyUpdate
          />
        </SparklineContainer>
      )}

      {hasLimits && (
        <LimitsRow>
          <LimitLabel color="#1b5e20">
            <ArrowDownwardIcon />
            Mín. {typeof min === 'number' ? min.toFixed(2) : min}
          </LimitLabel>
          <LimitLabel color="#B22222">
            <ArrowUpwardIcon />
            Máx. {typeof max === 'number' ? max.toFixed(2) : max}
          </LimitLabel>
        </LimitsRow>
      )}
    </CardWrapper>
  );
};

export default React.memo(KpiCard);
